<script setup lang="ts">
import { ref, watchEffect } from "vue";

const props = withDefaults(
	defineProps<{
		width?: string;
		height?: string;
		radius?: string;
		background?: string;
		value?: string;
		disabled?: boolean;
		mask?: (value: string) => void;
	}>(),
	{
		width: "100%",
		radius: "6px",
		background: "#fff",
	},
);

const emit = defineEmits(["update:modelValue"]);

const internalValue = ref<string | number | null>(null);

function update(event: any) {
	let value = event.target.value;
	if (props.mask) value = props.mask(value);

	internalValue.value = value;
	emit("update:modelValue", internalValue.value);
}

watchEffect(() => {
	if (props.value !== undefined)
		internalValue.value = props.value?.replace(/\\n/g, "\n");
});
</script>

<template>
	<div class="base-textarea">
		<div v-if="$slots.label" class="label">
			<slot name="label" />
		</div>
		<div class="content" :class="{ error: $slots.error }">
			<textarea
				v-bind="$attrs"
				v-model="internalValue"
				autocomplete="off"
				:disabled="disabled"
				@input="update"
			/>
		</div>
		<div v-if="$slots.error" class="error-message">
			<slot name="error" />
		</div>
	</div>
</template>

<style lang="scss" scoped>
$width: v-bind("width");
$radius: v-bind("radius");
$background: v-bind("background");

.base-textarea {
	display: flex;
	flex-direction: column;
	width: $width;
	gap: 2px;
	font-size: 1rem;

	.label {
		font-size: 0.875rem;
		margin-block: 0.2rem;
		margin-inline: 0.5rem;
	}

	.content {
		position: relative;
		display: flex;
		flex-direction: row;
		width: 100%;
		height: inherit;
		justify-content: center;
		align-items: center;
		border: 1px solid #d9d9d9;
		border-radius: $radius;
		background: $background;

		&.error {
			border-color: red;
		}
	}

	.error-message {
		position: relative;
		margin-block-end: -1.25rem;
		font-size: 0.875rem;
		padding-inline-start: $radius;
		color: red;
	}

	textarea {
		width: 100%;
		height: inherit;
		padding-inline: 10px;
		padding-block: 7px;
		outline: none;
		font-size: 1rem;
		border: 0;
		resize: none;
		background: transparent;
		font-family: "Open Sans";
		border-radius: $radius;

		&:disabled {
			cursor: not-allowed;
			color: var(--text-off);
			background: var(--white-off);
		}
	}
}
</style>
